import { Pressable } from "@/components/ui/pressable";
import type { OrganizationMember } from "@/components/atomic-crm/types";
import type { CalendarEvent } from "@/modules/calendar/calendarUtils";
import { CalendarEventAssigneeAvatars } from "@/modules/calendar/CalendarEventAssigneeAvatars";
import { getCalendarEventAssigneeColorClassName } from "@/modules/calendar/calendarAssigneeColors";
import {
  getEventChipLabel,
  getEventChipTooltip,
  getEventClassName,
} from "@/modules/calendar/calendarUtils";
import { cn } from "@/lib/utils";

export const CalendarEventChip = ({
  event,
  compact = false,
  onClick,
  static: isStatic = false,
  className: extraClassName,
  showAssigneeAvatars = false,
  membersById,
}: {
  event: CalendarEvent;
  compact?: boolean;
  onClick?: (event: CalendarEvent) => void;
  static?: boolean;
  className?: string;
  showAssigneeAvatars?: boolean;
  membersById?: Map<string, OrganizationMember>;
}) => {
  const typeClassName = getEventClassName(event);
  const assigneeClassName = getCalendarEventAssigneeColorClassName(event);
  const isMuted =
    typeClassName.includes("line-through") ||
    typeClassName.includes("text-muted-foreground");
  const className = cn(
    "block w-full rounded-sm px-1.5 text-left text-2xs font-medium leading-5 transition-opacity",
    compact ? "py-0" : "py-0.5",
    onClick && !isStatic && "hover:opacity-80",
    isMuted ? typeClassName : (assigneeClassName ?? typeClassName),
    extraClassName,
  );
  const label = getEventChipLabel(event);
  const tooltip = getEventChipTooltip(event);
  const content = (
    <span className="flex min-w-0 items-center gap-1">
      {showAssigneeAvatars && membersById ? (
        <CalendarEventAssigneeAvatars
          event={event}
          membersById={membersById}
          compact={compact}
        />
      ) : null}
      <span className="min-w-0 flex-1 truncate">{label}</span>
    </span>
  );

  if (isStatic || !onClick) {
    return (
      <div className={className} title={tooltip}>
        {content}
      </div>
    );
  }

  return (
    <Pressable
      onClick={(clickEvent) => {
        clickEvent.stopPropagation();
        onClick(event);
      }}
      className={className}
      title={tooltip}
    >
      {content}
    </Pressable>
  );
};
